// @ts-nocheck
import { hasRole } from "./roles";
/**
 * Side-nav item definitions and the visibility rules that gate them.
 *
 * Deliberately free of React and react-router imports so the rules can be unit-tested directly —
 * the same reasoning as utils/accounts.ts. The one that matters most: an `employeeOnly` entry
 * is INTERNAL ONLY and a customer must never be offered it.
 */

export type NavItem = {
  path: string;
  label: string;
  icon: string;
  hideWhenPending?: boolean;
  supportOnly?: boolean;
  /** AvSight internal staff only — driven by Copilot Employee Access on the Salesforce Contact. */
  employeeOnly?: boolean;
  /**
   * Marks the entry as a beta surface, which the side nav renders as a small flask badge.
   *
   * Separate from `employeeOnly` on purpose. employeeOnly answers "may this caller see it"; `beta`
   * answers "is this thing finished". They coincide today and will not always: a beta that opens
   * up to support admins is still beta, and an internal tool that is finished stops being beta
   * without ceasing to be internal.
   *
   * A beta entry is listed ONLY while Beta Preview is on (ACOP-163): Beta Preview is the explicit
   * preview mode that enables the employee access features. Beta Preview is itself employee-only
   * (utils/betaPreview.ts), so in practice that means an employee with the switch on.
   */
  beta?: boolean;
  /**
   * Hidden while Beta Preview is on (ACOP-163): the entry a beta entry is the upcoming replacement
   * for. Preview mode shows the future menu — Training in, AvSight University out — and switching
   * it off brings the current menu back. Like `beta`, it only ever takes effect for an employee.
   */
  hideWhenBeta?: boolean;
};

/**
 * The Learning group — the NEW experience, for everyone (ACOP-140, David, 2026-10-13).
 *
 * Home, Copilot and the Knowledge Base are the live product: the git-backed RAG and the in-app KB,
 * not the old Azure-search page or the Salesforce-hosted iframe KB. There is no Beta Preview swap and
 * no "(beta)" suffix or flask on these three. The paths keep their `-2` names on purpose: the AVS OS
 * KB and Home content link to /knowledge-base-2?article=…, so renaming them would break those links.
 * The old /copilot and /resources/knowledge-base still resolve — they redirect (utils/legacyRoutes.ts)
 * so nobody's bookmark dies.
 *
 * The `beta` flag on NavItem stays for OTHER beta features. The one entry here that uses it is
 * Training (the LMS), which is also employeeOnly.
 *
 * Training and AvSight University swap on Beta Preview (ACOP-163, David, 2026-11-04). Training is the
 * upcoming replacement for University, and Beta Preview is the explicit preview mode that enables the
 * employee access features: switch it ON and an employee sees Training and NOT University; switch it
 * OFF and they see University and NOT Training. Everyone else always sees University.
 */
export const PRIMARY: NavItem[] = [
  { path: "/home", label: "Home", icon: "fa-solid fa-house" },
  { path: "/copilot-2", label: "Copilot", icon: "fa-solid fa-book-open-reader" },
  { path: "/knowledge-base-2", label: "Knowledge Base", icon: "fa-solid fa-book-atlas" },
  // Training (ACOP-135): the LMS learner side, served by AVS OS. The upcoming replacement for AvSight
  // University (Northpass). Until it opens to customers it is an EMPLOYEE-ONLY beta (flask badge)
  // that exists only in Beta Preview. The copilot-api lms routes carry the same employee gate, and
  // the /learning PAGE needs the switch on as well (utils/betaPreview.ts PREVIEW_ONLY_ROUTES).
  {
    path: "/learning",
    label: "Training",
    icon: "fa-solid fa-chalkboard-user",
    employeeOnly: true,
    beta: true,
  },
  {
    path: "/resources/university",
    label: "AvSight University",
    icon: "fa-solid fa-graduation-cap",
    hideWhenBeta: true,
  },
];

export const ADDITIONAL_RESOURCES: NavItem[] = [
  {
    path: "/release-notes",
    label: "Change Log",
    icon: "fa-solid fa-clipboard-list",
  },
  {
    path: "/feature-requests",
    label: "Feature Voting",
    icon: "fa-solid fa-square-poll-vertical",
  },
];

export const ACCOUNT: NavItem[] = [
  {
    path: "/support-tickets",
    label: "Support Tickets",
    icon: "fa-solid fa-ticket-simple",
    supportOnly: true,
    hideWhenPending: true,
  },
  {
    path: "/change-requests",
    label: "Change requests",
    icon: "fa-solid fa-clipboard-list",
    supportOnly: true,
    hideWhenPending: true,
  },
  {
    path: "/upgrade-requests",
    label: "Upgrade Requests",
    icon: "fa-solid fa-circle-arrow-up",
    supportOnly: true,
    hideWhenPending: true,
  },
  {
    path: "/my-subscription",
    label: "My Subscription",
    icon: "fa-solid fa-file-signature",
    supportOnly: true,
    hideWhenPending: true,
  },
  {
    path: "/discover-addons",
    label: "Add-On Services",
    icon: "fa-solid fa-cart-plus",
    supportOnly: true,
  },
  {
    path: "/early-adopters",
    label: "Early Adopters",
    icon: "fa-solid fa-stream",
    supportOnly: true,
  },
];

/**
 * Which items a caller may see.
 *
 * Fails CLOSED: hasRole returns false for a missing, malformed or non-array roles value, so a
 * failed role refresh hides gated items rather than revealing them.
 *
 * `betaPreviewOn` is the RESOLVED switch state (utils/betaPreview.ts isBetaPreviewOn), so the menu
 * re-renders the moment it flips — no reload. The preview mode is checked here again against the
 * employee role, so it takes effect only for an employee and only on a literal `true`: a caller that
 * forgets the argument, or passes a stale flag for a non-employee, gets the ordinary menu (University
 * listed, no beta entries). A `beta` entry is listed only in preview mode; a `hideWhenBeta` entry is
 * hidden only in preview mode.
 */
export function filterItems(items: NavItem[], roles: unknown[], reqStatus: string, betaPreviewOn: boolean = false) {
  const previewMode = betaPreviewOn === true && hasRole(roles, "employee");
  return items.filter((item) => {
    if (item.supportOnly && !hasRole(roles, "support")) return false;
    if (item.employeeOnly && !hasRole(roles, "employee")) return false;
    if (item.hideWhenPending && reqStatus === "Requested") return false;
    if (item.beta && !previewMode) return false;
    if (item.hideWhenBeta && previewMode) return false;
    return true;
  });
}
